import React from "react";
import { isOnToolTipDebugging } from "@src/util/experiment.util";
import { getAttendeesPainText } from "@clockwise/client-commons/src/util/scheduling-slot";
import { InfoTip } from "@components/tool-tip";
import { AttendeesPainsTailwind } from "@clockwise/web-commons/src/components/attendees-pains/AttendeesPains";
import {
  EventSchedulingQueryEvent,
  EventSchedulingQueryOrg,
  EventSchedulingQueryPerson,
  getOrderedAttendeesAndPersonsMaps,
  rowClasses,
} from "./EventScheduling";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { ResponseStatusEnum } from "@clockwise/schema";

const painClasses = "cw-text-peach-darker cw-font-bold";

const AttendeesSlotPainsSummary = ({
  org,
  event,
  persons,
  highlightPains = false,
}: {
  org: EventSchedulingQueryOrg | null | undefined;
  event: EventSchedulingQueryEvent | undefined;
  persons: EventSchedulingQueryPerson[];
  highlightPains?: boolean;
}) => {
  if (!org || !event) {
    return null;
  }
  const computeCurrentSlotImpactErrorable = getValue(org.computeCurrentSlotImpactErrorable);
  if (!computeCurrentSlotImpactErrorable) {
    return null;
  }

  const currentSlot = getValue(computeCurrentSlotImpactErrorable?.currentSlot);

  const { attendeeMap } = getOrderedAttendeesAndPersonsMaps({
    org: org,
    event,
  });

  // The BE always returns conflicts even for attendees who have declined a meeting
  // but we want to render pains only when attendees have not declined
  // Thus manually strip out the conflict data for any declined attendees here before generating pains text
  const currentSlotWithoutConflictFromAttendeesWhoHaveDeclined = {
    ...currentSlot,
    attendanceImpacts:
      currentSlot?.attendanceImpacts.filter(
        // TODO: (jimmy) filter out rooms from the list of attendees
        (ai) => attendeeMap[ai.calendarId]?.responseStatus != ResponseStatusEnum.Declined,
      ) || [],
    summary: {
      ...currentSlot?.summary,
      conflictingAttendees:
        currentSlot?.summary?.conflictingAttendees?.filter(
          (calendarId) => attendeeMap[calendarId]?.responseStatus != ResponseStatusEnum.Declined,
        ) || [],
    },
  } as typeof currentSlot;

  const pains =
    currentSlotWithoutConflictFromAttendeesWhoHaveDeclined && persons
      ? getAttendeesPainText(currentSlotWithoutConflictFromAttendeesWhoHaveDeclined, persons, true)
      : [];

  const AttendeesPains = (
    <AttendeesPainsTailwind
      pains={pains}
      rowClasses={rowClasses}
      painClasses={highlightPains ? painClasses : ""}
    />
  );

  if (!isOnToolTipDebugging(org)) {
    return AttendeesPains;
  }

  return (
    <InfoTip
      targetElement={AttendeesPains}
      text={<div>{JSON.stringify(currentSlot)}</div>}
      tipType={"info"}
    />
  );
};

export default AttendeesSlotPainsSummary;
